<script lang="ts">
  import { preventDefault } from 'svelte/legacy';

  // Component for new style of customer service banner
  import { page } from "$app/stores";
  import Button from "./Button.svelte";
  import Input from "./InputText.svelte";
  import { callbackFunctionIsDisabled } from "./utils/featureToggleUtils";
  import sessionValues from "$lib/sessionValues";
  import { getUtmSearchParams } from "./utils/utmSearchParams";

  const eightDigitsNotStartingWith0Or1 = `^[2-9]\\d{7}$`;
  const callbackDisabled = callbackFunctionIsDisabled();

  const invalidTelEvent = (e: Event) => {
    const eventTarget = e.currentTarget as HTMLInputElement;

    // Set custom error message
    if (eventTarget?.validity?.valueMissing) {
      eventTarget.setCustomValidity("Mobilnummer er påkrevd");
    } else if (eventTarget?.validity?.patternMismatch) {
      eventTarget.setCustomValidity(
        "Mobilnummeret må bestå av 8 siffer, og være et gyldig norsk mobilnummer"
      );
    }
  };

  const invalidTelMessageReset = (e: Event) => {
    const eventTarget = e.currentTarget as HTMLInputElement;
    // Reset custom error message
    if (eventTarget) eventTarget.setCustomValidity("");
  };

  async function sendForm() {
    const data = new FormData(form);
    const mobile = data.get("mobile");
    data.append("origin", $page.url.pathname);
    data.append("mobile", `+47${mobile}`);
    const source = $page.url.searchParams.get("source") || "ishavskraft.no";
    data.append("source", source);
    data.append("trigger", "beige-banner");
    const utmParams = getUtmSearchParams($sessionValues);
    for (const [key, value] of utmParams) {
      data.append(key, value);
    }
    const body = Object.fromEntries(data.entries());

    const res = await fetch("/api/privat/ringmeg", {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (res.ok) {
      form.reset();
      formResponse =
        customFeedback ??
        `Takk for henvendelsen, vi kontakter deg på ${
          mobile ? mobile.toString().replace(/(\d{3})(\d{2})(\d{3})/, "+47 $1 $2 $3") : "telefon"
        } så snart en av våre kunderådgivere blir ledig!`;
    } else {
      formResponse = "Noe gikk galt. Vennligst prøv igjen eller kontakt oss om problemet vedvarer.";
    }
  }

  

  interface Props {
    lang?: string | null;
    title?: string | null;
    phoneLabel?: string | null;
    emailLabel?: string | null;
    contactForm?: boolean;
    customPhone?: string | undefined;
    customEmail?: string | undefined;
    customFeedback?: string | undefined;
    showNameInput?: boolean;
    showMessageInput?: boolean;
    class?: string;
    children?: import('svelte').Snippet;
  }

  let {
    lang = null,
    title = null,
    phoneLabel = null,
    emailLabel = null,
    contactForm = true,
    customPhone = undefined,
    customEmail = undefined,
    customFeedback = undefined,
    showNameInput = true,
    showMessageInput = true,
    class: classes = "",
    children
  }: Props = $props();
  let form: HTMLFormElement = $state();
  let formResponse: string | undefined = $state();

  let contactPhone = $derived(customPhone ?? "776 12 000");
  let contactEmail = $derived(customEmail ?? "kunde@ishavskraft.no");
</script>

<section
  class="info_container py-4 mb-12 bg-$primary-white {classes} rounded-2xl mx-edge-x px-3 tablet:px-5"
  {lang}
>
  <h2 class="hyphens-manual mb-0">{title || "Har du andre spørsmål, ta gjerne kontakt med oss"}</h2>

  <address class="not-italic text-body children-my-0">
    <p class="mb-2">
      {phoneLabel || "Ring oss på"}
      <a class="hyphens-manual" href="tel:{contactPhone}">{contactPhone}</a>
    </p>
    <p>
      {emailLabel || "Send oss en mail på"}
      <a class="hyphens-manual" href="mailto:{contactEmail}">{contactEmail}</a>
    </p>
  </address>

  {#if contactForm && !callbackDisabled}
    <p class="text-body mt-2">Eller vi kan kontakte deg</p>
    <form class="callback" method="POST" onsubmit={preventDefault(sendForm)} bind:this={form}>
      <p class:wrapper-grid={showNameInput} class="col-span-2 gap-x-4 m-0">
        <label for="mobile-input">Mobilnummer</label>
        <Input
          type="tel"
          name="mobile"
          id="mobile-input"
          placeholder="Ditt mobilnummer"
          required
          pattern={eightDigitsNotStartingWith0Or1}
          on:invalid={invalidTelEvent}
          on:input={invalidTelMessageReset}
        />

        {#if showNameInput}
          <label for="name-input">Navn</label>
          <Input
            type="text"
            name="name"
            id="name-input"
            placeholder="Ditt navn (Valgfri)"
            autocomplete="off"
          />
        {/if}
      </p>

      {#if showMessageInput}
        <p class="col-span-2 m-0">
          <label for="message-input">Hva gjelder det?</label>
          <Input
            class="w-full"
            name="message"
            id="message-input"
            placeholder="Stikkord (Valgfri)"
            autocomplete="off"
          />
        </p>
      {/if}

      <p class="col-span-2 m-0">
        <Button type="submit" class="w-full whitespace-nowrap">Send inn</Button>
      </p>
    </form>

    {#if formResponse}
      <p>{formResponse}</p>
    {/if}
  {/if}

  {@render children?.()}
</section>

<style>
  .info_container {
    display: flex;
    flex-direction: column;
  }

  a {
    font-weight: 600;
  }

  .callback {
    display: grid;
    gap: var(--s-4);
    padding-block-end: var(--s-2);
    max-width: var(--w-prose);
  }

  .wrapper-grid {
    display: grid;
    grid-template-rows: auto auto;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-flow: column;
  }

  label {
    display: block;
    margin-block-end: var(--s-1);
  }

  @media (--tablet) {
    .callback {
      display: flex;
      align-items: flex-end;
    }
  }
</style>
